<script setup>
definePage({
  meta: {
    action: 'write',
    subject: 'source-user',
    navActiveLink: 'sources-users',
  },
})

import { getProjectId } from '@/utils/cdms'
import { useSnackBar } from '@/utils/stores/snackbar'

const router = useRouter()
const snackbarStore = useSnackBar()

if (await isLockedProject()) {
  router.back()
}

const siteSelects = ref([])
const roles = ref([])            // [{ id:number, label:string, name, realName }]
const data = reactive({})        // 사용자 상세
const id = router.currentRoute.value.params.id

/* ---------------- 역할 목록 ---------------- */
const { data: roleData, error: roleErr } = await useApi(createUrl('/management/roles/all'))
if (roleErr?.value) {
  // 필요 시 에러 처리
  // console.error(roleErr.value)
  roles.value = []
} else {
  const arr = Array.isArray(roleData.value) ? roleData.value : []


  // ⚠️ .slice(1)로 잘라내면 현재 사용자의 역할이 누락될 수 있으니 지움
  roles.value = arr.map(r => ({
    ...r,
    id: Number(r.id),                           // id는 숫자로 고정
    label: r.realName ?? r.name ?? '',          // 표시용 라벨(빈값 방지)
  }))
}

/* ---------------- 사이트 목록 ---------------- */
const { data: siteData } = await useApi(createUrl('/sources/sites/all', {
  query: { projectId: getProjectId() },
}))

siteSelects.value = Array.isArray(siteData.value) ? siteData.value : []

/* ---------------- 사용자 상세 ---------------- */
const { data: user } = await useApi(createUrl(`/sources/users/${id}`))

Object.assign(data, camelCase(user.value || {}))

/* ---------------- 유틸: roleId 정규화 ---------------- */
// "2" -> 2, "시험책임자/담당자"(라벨) -> 해당 id, 못 찾으면 null
function normalizeRoleId(v, list) {
  if (v == null) return null

  // 숫자/숫자문자 우선
  const n = Number(v)
  if (!Number.isNaN(n)) return n


  // 라벨/이름으로 넘어온 경우 매칭
  const hit = (list || []).find(
    x => x.label === v || x.realName === v || x.name === v,
  )
  
  return hit ? hit.id : null
}

/* ---------------- 초기 roleId 맞추기 ---------------- */
let normalizedOnce = false
watchEffect(() => {
  if (normalizedOnce) return
  if (!roles.value.length) return          // roles가 아직이면 대기
  // data.roleId가 존재하면 정규화 시도
  if ('roleId' in data) {
    const fixed = normalizeRoleId(data.roleId, roles.value)
    if (fixed !== data.roleId) data.roleId = fixed
  } else {
    // 상세에 roleId 키가 아예 없을 경우를 대비
    data.roleId = null
  }
  normalizedOnce = true
})

/* ---------------- 표시용 역할/기관명 ---------------- */
const roleLabel = computed(() => {
  const r = roles.value.find(x => x.id === data.roleId)
  return r?.label ?? ''
})
const siteLabel = computed(() => {
  const s = siteSelects.value.find(x => x.id === data.siteId)
  return s?.name ?? ''
})

/* ---------------- 저장 ---------------- */
const refForm = ref(null)

const saveUser = async () => {
  const validate = await refForm?.value.validate()
  if (!validate.valid) return

  // 저장 직전에도 보정(혹시 모를 타입 꼬임 방지)
  const fixedRoleId = normalizeRoleId(data.roleId, roles.value)
  const payload = { ...data, roleId: fixedRoleId }

  await $api(`/sources/users`, {
    method: 'PATCH',
    body: { projectId: getProjectId(), data: payload },
  })

  snackbarStore.showSnackBar('Successfully Saved!')
  router.push({ name: 'sources-users' })
}
</script>

<template>
  <VRow>
    <VCol
      cols="12"
      sm="12"
      md="9"
      lg="7"
      order="1"
      order-lg="1"
    >
      <VCard :title="$t('Edit User')">
        <VCardText class="mt-4">
          <VForm
            ref="refForm"
            @submit.prevent="() => {}"
          >
            <VRow>
              <!-- Email -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="email"
                    >
                      {{ $t('User') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="email"
                      v-model="data.email"
                      persistent-placeholder
                      disabled
                      :rules="[requiredValidator]"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Name -->
              <VCol
                v-if="data.email"
                cols="12"
              >
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="name"
                    >
                      {{ $t('User Name') }}<span style="color:red">*</span>
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      id="name"
                      v-model="data.name"
                      persistent-placeholder
                      disabled
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Role (읽기전용) -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">
                      {{ $t('Role') }}
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      :model-value="roleLabel"
                      persistent-placeholder
                      disabled
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Site (읽기전용) -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label class="v-label text-body-2 text-high-emphasis">
                      {{ $t('Site Name') }}
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppTextField
                      :model-value="siteLabel"
                      persistent-placeholder
                      disabled
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Start Date -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="startDate"
                    >
                      {{ $t('Start Date') }}
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppDateTimePicker
                      v-model="data.startDate"
                      append-inner-icon="tabler-calendar-event"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- End Date -->
              <VCol cols="12">
                <VRow no-gutters>
                  <VCol
                    cols="12"
                    md="3"
                    class="d-flex align-items-center"
                  >
                    <label
                      class="v-label text-body-2 text-high-emphasis"
                      for="endDate"
                    >
                      {{ $t('End Date') }}
                    </label>
                  </VCol>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <AppDateTimePicker
                      v-model="data.endDate"
                      append-inner-icon="tabler-calendar-event"
                    />
                  </VCol>
                </VRow>
              </VCol>

              <!-- Actions -->
              <VCol
                offset-md="3"
                cols="12"
                md="9"
                class="d-flex gap-4"
              >
                <VBtn
                  type="submit"
                  @click="saveUser"
                >
                  {{ $t('Save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  @click="$router.back()"
                >
                  {{ $t('Cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>
